import { LANGUAGE_CN } from '@intelligence/_config/config';

/**
 * 兜底模式：
 * - filePosition：基于文件位置推断 Chinese / English（AI Hub / Insight / ICC / Game Research 的旧逻辑）
 * - reportLanguage：使用 report_language_en 字段（Market 等的旧逻辑）
 */
export type TLangFallbackMode = 'filePosition' | 'reportLanguage';

/**
 * 兼容读取报告语言字段：
 * 1. 有新字段 file_cn_language / file_en_language → 直接使用（各文件独立语言）
 *    a. 若两个字段值相同且含逗号（双语检测结果），拆分后第一语种→cn，第二语种→en
 *    b. 若两个字段为不同的单值 → 各自独立使用
 *    c. 其他多值边界情况（不同多值 / 单边多值）→ 跳过，走兜底
 * 2. 无新字段 → 按 fallbackMode 走各页面的旧逻辑
 */
export function getReportLanguages(
  report: Record<string, any>,
  fallbackMode: TLangFallbackMode = 'filePosition',
): {
    fileCnLang: string;
    fileEnLang: string;
  } {
  const newCnLang = report?.file_cn_language || '';
  const newEnLang = report?.file_en_language || '';

  if (newCnLang || newEnLang) {
    const cnHasMulti = newCnLang.includes(',');
    const enHasMulti = newEnLang.includes(',');

    if (cnHasMulti && newCnLang === newEnLang) {
      const langs = newCnLang.split(',').map((l: string) => l.trim()).filter(Boolean);
      if (langs.length >= 2) {
        return {
          fileCnLang: report?.pdf ? langs[0] : '',
          fileEnLang: report?.pdf_en ? langs[1] : '',
        };
      }
    }

    if (!cnHasMulti && !enHasMulti) {
      return { fileCnLang: newCnLang, fileEnLang: newEnLang };
    }
  }

  if (fallbackMode === 'reportLanguage') {
    const reportLang = report?.report_language || '';
    const reportLangEn = report?.report_language_en || '';
    if (reportLang || reportLangEn) {
      const langValue = reportLangEn || reportLang;
      return {
        fileCnLang: report?.pdf ? langValue : '',
        fileEnLang: report?.pdf_en ? langValue : '',
      };
    }
  }

  return {
    fileCnLang: report?.pdf ? 'Chinese' : '',
    fileEnLang: report?.pdf_en ? 'English' : '',
  };
}

/**
 * normalizedLang → i18n key 映射，用于语言 Tab 文案国际化。
 * 找不到映射时由调用方兜底为 `${lang} Report`。
 */
export const LANG_TAB_I18N_MAP: Record<string, string> = {
  English: 'dbi_segment_english_report',
  French: 'dbi_segment_french_report',
  Italian: 'dbi_segment_italian_report',
  German: 'dbi_segment_german_report',
  Spanish: 'dbi_segment_spanish_report',
  Chinese: 'dbi_segment_chinese_report',
  Japanese: 'dbi_segment_japanese_report',
  Korean: 'dbi_segment_korean_report',
  Portuguese: 'dbi_segment_portuguese_report',
  Russian: 'dbi_segment_russian_report',
  Polish: 'dbi_segment_polish_report',
  Arabic: 'dbi_segment_arabic_report',
  Turkish: 'dbi_segment_turkish_report',
  Thai: 'dbi_segment_thai_report',
  Vietnamese: 'dbi_segment_vietnamese_report',
  Indonesian: 'dbi_segment_indonesian_report',
};

/** 统一语言值大小写 */
export function normalizeLanguage(lang: string): string {
  if (!lang) return '';
  const lower = lang.trim().toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * 从报告列表中提取去重的语言 Tab 选项
 * @param buildText 可选，用于构建 Tab 展示文案（传入 normalizedLang），
 * 不传时默认使用 `${lang} Report`
 */
export function collectLanguageTabs(
  reports: Record<string, any>[],
  fallbackMode: TLangFallbackMode = 'filePosition',
  buildText?: (normalizedLang: string) => string,
): Array<{ value: string; text: string; }> {
  const langSet = new Map<string, string>();
  for (const r of reports) {
    const { fileCnLang, fileEnLang } = getReportLanguages(r, fallbackMode);
    if (fileCnLang) langSet.set(normalizeLanguage(fileCnLang), fileCnLang);
    if (fileEnLang) langSet.set(normalizeLanguage(fileEnLang), fileEnLang);
  }
  const PRIORITY_LANGS: Record<string, number> = { English: 0, Chinese: 1 };
  const sorted = [...langSet.entries()].sort((a, b) => {
    const pa = PRIORITY_LANGS[a[0]] ?? 2;
    const pb = PRIORITY_LANGS[b[0]] ?? 2;
    if (pa !== pb) return pa - pb;
    return a[0].localeCompare(b[0]);
  });
  return sorted.map(([norm]) => ({
    value: norm,
    text: buildText ? buildText(norm) : `${norm} Report`,
  }));
}

/** 根据选中的语言 Tab 和报告数据，决定打开 'cn' 或 'en' 文件 */
export function getReportLangForFile(
  report: Record<string, any>,
  selectedLang: string,
  locale: string,
  fallbackMode: TLangFallbackMode = 'filePosition',
): 'en' | 'cn' {
  const pdf = report?.pdf || '';
  const pdfEn = report?.pdf_en || '';

  if (!selectedLang || selectedLang === 'all') {
    if (pdf && !pdfEn) return 'cn';
    if (pdfEn && !pdf) return 'en';
    return locale === LANGUAGE_CN ? 'cn' : 'en';
  }

  const { fileCnLang, fileEnLang } = getReportLanguages(report, fallbackMode);
  const normalizedSelected = normalizeLanguage(selectedLang);
  const cnMatch =
    normalizeLanguage(fileCnLang) === normalizedSelected && !!pdf;
  const enMatch =
    normalizeLanguage(fileEnLang) === normalizedSelected && !!pdfEn;

  if (cnMatch && enMatch) {
    if (normalizedSelected === 'English') return 'en';
    if (normalizedSelected === 'Chinese') return 'cn';
    return 'en';
  }

  if (cnMatch) return 'cn';
  if (enMatch) return 'en';

  return locale === LANGUAGE_CN ? 'cn' : 'en';
}
